import { Link } from "react-router-dom";
import { type BhgCallRecord, type BhgScheduleSummary } from "../bhg-call-dashboard-api";
import type { BhgCallDashboardProvider, BhgClinicOption } from "../types";
import { BhgCallUserDashboardPageV2 } from "./bhg-call-user-dashboard-page.v2";
import { BHG_OFFERING_ID, BHG_ROUTES } from "@constructorfleet/bhg-product-core";

const addSeconds = (date: Date, seconds: number): Date => {
  const newDate = new Date(date);
  newDate.setSeconds(newDate.getSeconds() + seconds);
  return newDate;
};

const IVR_PREAMBLE =
  "thank you for calling behavioral health group huntsville treatment center if this is a medical emergency please hang up and call 911 if you are calling for information about becoming a patient please press 1 if you are an existing patient please press [keys: 2] for the medication call back line back line please line please press [keys: 1] please select 1 for spanish or remain on the line for english. please enter enter only the numeric digits of your patient id followed by the pound key [keys: 1234#]. please enter the last 4 digits of your social security number followed by the pound key [keys: 6789#]. your client id is 1234 the last 4 digits of your social security number 6789. please say yes if your information is correct otherwise please say no [keys: 1]. ";

const notScheduled = (dateLong: string) =>
  `${IVR_PREAMBLE}you are not scheduled to come into the treatment center on ${dateLong}. Please call on your next scheduled calendar date. thank you for calling`;

const drugScreen = (dateLong: string) =>
  `${IVR_PREAMBLE}You are scheduled to visit the treatment center by the end of dosing hours on ${dateLong} for a urine screen please note that failure to comply with this request may affect your treatment status.`;

const bottleCheck = (dateLong: string) =>
  `${IVR_PREAMBLE}you are scheduled to visit the treatment center by the end of dosing hours on ${dateLong} please bring all your prescribed medication bottles whether full or empty with you. thank you for calling bhg.`;

const DEMO_AUDIO: Record<string, string> = {
  "CA-DEMO-001": "/audio/diversion check - 2026-04-22.wav",
  "CA-DEMO-002": "/audio/Not Scheduled - 2026-04-21.wav",
  "CA-DEMO-003": "/audio/Drug Screen - 2026-04-20.wav",
  "CA-DEMO-004": "/audio/Not Scheduled - 2026-04-19.wav",
  "CA-DEMO-005": "/audio/Not Scheduled - 2026-04-18.wav",
  "CA-DEMO-006": "/audio/Not Scheduled - 2026-04-17.wav",
  "CA-DEMO-007": "/audio/Not Scheduled - 2026-04-16.wav",
  "CA-DEMO-008": "/audio/Not Scheduled - 2026-04-15.wav",
  "CA-DEMO-009": "/audio/Not Scheduled - 2026-04-14.wav",
};

const DEMO_CALLS: BhgCallRecord[] = [
  {
    id: 1,
    userId: 1,
    startedAt: "2026-04-22T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Bottle Check",
    resultDate: "2026-04-23",
    callSid: "CA-DEMO-001",
    transcript: bottleCheck("April 23rd, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 2,
    userId: 1,
    startedAt: "2026-04-21T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-21",
    callSid: "CA-DEMO-002",
    transcript: notScheduled("April 21st, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 3,
    userId: 1,
    startedAt: "2026-04-20T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Drug Screen",
    resultDate: "2026-04-20",
    callSid: "CA-DEMO-003",
    transcript: drugScreen("April 20th 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 4,
    userId: 1,
    startedAt: "2026-04-19T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-19",
    callSid: "CA-DEMO-004",
    transcript: notScheduled("April 19th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 5,
    userId: 1,
    startedAt: "2026-04-18T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-18",
    callSid: "CA-DEMO-005",
    transcript: notScheduled("April 18th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 6,
    userId: 1,
    startedAt: "2026-04-17T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-17",
    callSid: "CA-DEMO-006",
    transcript: notScheduled("April 17th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 7,
    userId: 1,
    startedAt: "2026-04-16T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-16",
    callSid: "CA-DEMO-007",
    transcript: notScheduled("April 16th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 8,
    userId: 1,
    startedAt: "2026-04-15T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-15",
    callSid: "CA-DEMO-008",
    transcript: notScheduled("April 15th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
  {
    id: 9,
    userId: 1,
    startedAt: "2026-04-14T07:00:00.000-06:00",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-04-14",
    callSid: "CA-DEMO-009",
    transcript: notScheduled("April 14th, 2026"),
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
  },
];

const DEMO_SCHEDULE: BhgScheduleSummary = {
  todayIsoDate: "2026-04-23",
  tomorrowIsoDate: "2026-04-24",
  today: { scheduledLabels: ["Drug Screen"], unscheduledLabels: [] },
  tomorrow: { scheduledLabels: [], unscheduledLabels: [] },
};

async function fetchClinics(): Promise<BhgClinicOption[]> {
  const sessionToken = localStorage.getItem("cf-session-token");
  const headers: Record<string, string> = { Accept: "application/json" };
  if (sessionToken) headers["x-session-token"] = sessionToken;
  const response = await fetch("/api/website/bhg-clinics", { headers });
  if (!response.ok) return [];
  const data = (await response.json()) as { clinics?: BhgClinicOption[] };
  return data.clinics ?? [];
}

export const demoBhgCallDashboardProvider: BhgCallDashboardProvider = {
  fetchCalls: async () => ({
    calls: DEMO_CALLS,
    scheduleSummary: DEMO_SCHEDULE,
    updatedAt: new Date().toISOString(),
  }),
  subscribeCalls: () => () => undefined,
  fetchRecordingAvailability: async () => ({ available: true, note: null }),
  buildRecordingDownloadUrl: (callSid) => DEMO_AUDIO[callSid] ?? `#demo-recording-${callSid}`,
  fetchCallEvents: async (callSid) => ({
    events: [
      {
        id: 1,
        callSid,
        createdAt:
          DEMO_CALLS.find((call) => call.callSid === callSid)?.startedAt ??
          new Date().toISOString(),
        callStatus: "queued",
        callDurationSeconds: 78,
        transcript: DEMO_CALLS.find((call) => call.callSid === callSid)?.transcript ?? "",
        payloadKeys: ["CallSid", "CallStatus"],
      },
      {
        id: 2,
        callSid,
        createdAt: addSeconds(
          new Date(
            DEMO_CALLS.find((call) => call.callSid === callSid)?.startedAt ??
              new Date().toISOString()
          ),
          5
        ).toISOString(),
        callStatus: "initiated",
        callDurationSeconds: 78,
        transcript: DEMO_CALLS.find((call) => call.callSid === callSid)?.transcript ?? "",
        payloadKeys: ["CallSid", "CallStatus"],
      },
      {
        id: 3,
        callSid,
        createdAt: addSeconds(
          new Date(
            DEMO_CALLS.find((call) => call.callSid === callSid)?.startedAt ??
              new Date().toISOString()
          ),
          7
        ).toISOString(),
        callStatus: "in-progress",
        callDurationSeconds: 78,
        transcript: DEMO_CALLS.find((call) => call.callSid === callSid)?.transcript ?? "",
        payloadKeys: ["CallSid", "CallStatus"],
      },
      {
        id: 4,
        callSid,
        createdAt: addSeconds(
          new Date(
            DEMO_CALLS.find((call) => call.callSid === callSid)?.startedAt ??
              new Date().toISOString()
          ),
          78
        ).toISOString(),
        callStatus: "completed",
        callDurationSeconds: 78,
        transcript: DEMO_CALLS.find((call) => call.callSid === callSid)?.transcript ?? "",
        payloadKeys: ["CallSid", "CallStatus"],
      },
    ],
    updatedAt: new Date().toISOString(),
  }),
  fetchClinics: fetchClinics,
  fetchSubscription: async () => ({
    products: [BHG_OFFERING_ID],
    bhgEnrollment: {
      userId: 1,
      clinicId: 5,
      clinicName: "BHG Huntsville Treatment Center",
      notificationTime: "05:00",
      notificationTimezone: "America/New_York",
      mobilePhone: "3035550000",
      notificationEmail: "demo@example.com",
      notificationChannels: ["sms"],
      clinicDosingHours:
        '{"monday":"5:00 am - 11:45 am","tuesday":"5:00 am - 11:45 am","wednesday":"5:00 am - 11:45 am","thursday":"5:00 am - 11:45 am","friday":"5:00 am - 11:45 am","saturday":"6:00 am - 8:45 am","sunday":"6:00 am - 8:45 am"}',
      clinicOperatingHours:
        '{"monday":"5:00 am - 1:00 pm","tuesday":"5:00 am - 1:00 pm","wednesday":"5:00 am - 1:00 pm","thursday":"5:00 am - 1:00 pm","friday":"5:00 am - 1:00 pm","saturday":"6:00 am - 10:00 am","sunday":"6:00 am - 10:00 am"}',
    },
  }),
  fetchUserProfile: async () => null,
  updateClinic: async () => true,
  updateNotifications: async () => true,
  updateNotificationSchedule: async () => true,
};

export function BhgCallDemoDashboardPage() {
  return (
    <div style={{ position: "relative" }}>
      <BhgCallUserDashboardPageV2 provider={demoBhgCallDashboardProvider} />
      <div className="demo-watermark">
        <span className="demo-watermark-label">Demo</span>
        <span className="demo-watermark-copy">This is sample data.</span>
        <Link className="btn btn-brand btn-sm" to={`/${BHG_ROUTES.SIGNUP}`}>
          Get started with Check-in Assist
        </Link>
      </div>
    </div>
  );
}
